const BebeomiSearchIndex = (() => {
  // Client-side catalogue index so the dropdown matches what shoppers type
  // (product names, brands, typos) instead of relying only on Shopify's
  // semantic ranking, which surfaced loosely related products.
  const STORAGE_KEY = 'bebeomi-search-index-v1';
  const TTL = 10 * 60 * 1000;
  const MAX_RESULTS = 8;
  let pending = null;

  const normalize = (value) =>
    (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const words = (value) => normalize(value).split(' ').filter(Boolean);

  const prepare = (products) =>
    products.map((product) => ({
      ...product,
      titleWords: words(product.t),
      extraWords: words(product.x),
      titleJoined: words(product.t).join(''),
    }));

  const pageUrl = (page) =>
    `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}collections/all?view=search-index&page=${page}`;

  const fetchPage = (page) =>
    fetch(pageUrl(page), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => JSON.parse(text));

  function load() {
    if (pending) return pending;
    try {
      const cached = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      if (cached && Date.now() - cached.savedAt < TTL && Array.isArray(cached.products)) {
        pending = Promise.resolve(prepare(cached.products));
        return pending;
      }
    } catch (error) {
      // Storage unavailable; fall through to the network.
    }

    pending = fetchPage(1)
      .then((first) => {
        const requests = [];
        const pages = Math.min(parseInt(first.pages, 10) || 1, 40);
        for (let page = 2; page <= pages; page++) requests.push(fetchPage(page));
        return Promise.all(requests).then((rest) =>
          first.products.concat(...rest.map((result) => result.products))
        );
      })
      .then((products) => {
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), products }));
        } catch (error) {
          // Ignore quota or privacy-mode errors.
        }
        return prepare(products);
      })
      .catch((error) => {
        pending = null;
        throw error;
      });

    return pending;
  }

  function distance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i++) {
      const current = [i];
      let rowMin = i;
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
        rowMin = Math.min(rowMin, current[j]);
      }
      if (rowMin > max) return max + 1;
      previous = current;
    }
    return previous[b.length];
  }

  function tokenScore(token, candidates) {
    let best = 0;
    const allowed = token.length <= 3 ? 0 : token.length <= 5 ? 1 : token.length <= 8 ? 2 : 3;
    for (const word of candidates) {
      let score = 0;
      if (word === token) score = 100;
      else if (word.startsWith(token)) score = 90 - Math.min(20, word.length - token.length);
      else if (token.length >= 3 && word.includes(token)) score = 65;
      else if (allowed > 0) {
        const typo = Math.min(
          distance(token, word, allowed),
          word.length > token.length ? distance(token, word.slice(0, token.length), allowed) : allowed + 1
        );
        if (typo <= allowed) score = 55 - typo * 10;
      }
      if (score > best) best = score;
      if (best === 100) break;
    }
    return best;
  }

  function rank(query, products) {
    const tokens = words(query);
    if (!tokens.length) return [];
    const joinedQuery = tokens.join('');
    const scored = [];

    for (const product of products) {
      let matched = 0;
      let score = 0;
      for (const token of tokens) {
        const titleScore = tokenScore(token, product.titleWords);
        const extraScore = titleScore < 90 ? tokenScore(token, product.extraWords) * 0.6 : 0;
        const tokenBest = Math.max(titleScore, extraScore);
        if (tokenBest > 0) {
          matched++;
          score += tokenBest;
        }
      }
      if (joinedQuery.length >= 3 && product.titleJoined.includes(joinedQuery)) {
        matched = tokens.length;
        score += 50;
      }
      if (matched) scored.push({ product, matched, score });
    }

    if (!scored.length) return [];
    const bestMatched = Math.max(...scored.map((entry) => entry.matched));
    const required = bestMatched === tokens.length ? tokens.length : Math.max(1, Math.ceil(tokens.length / 2));

    return scored
      .filter((entry) => entry.matched >= required)
      .sort(
        (a, b) =>
          b.matched - a.matched ||
          b.score - a.score ||
          Number(b.product.a) - Number(a.product.a) ||
          a.product.t.length - b.product.t.length
      )
      .slice(0, MAX_RESULTS)
      .map((entry) => entry.product);
  }

  return { load, rank };
})();

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    BebeomiSearchIndex.load().catch(() => {});
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'Escape') {
      event.preventDefault();
      this.close();
      this.input.focus();
      return;
    }

    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    const nativeResults = fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    ).then((response) => {
      if (!response.ok) {
        var error = new Error(response.status);
        this.close();
        throw error;
      }

      return response.text();
    });
    const localIndex = BebeomiSearchIndex.load().catch(() => null);

    Promise.all([nativeResults, localIndex])
      .then(([text, index]) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (index) this.applyLocalProductMatches(section, searchTerm, index);
        const resultsMarkup = section.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  applyLocalProductMatches(section, searchTerm, index) {
    const matches = BebeomiSearchIndex.rank(searchTerm, index);
    const root = section?.querySelector('#predictive-search-results');
    if (!matches.length || !root) return;

    let wrapper = root.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.id = 'predictive-search-results-groups-wrapper';
      wrapper.className =
        'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
      root.prepend(wrapper);
    }
    wrapper.classList.remove('predictive-search__results-groups-wrapper--no-products');

    let list = wrapper.querySelector('#predictive-search-results-products-list');
    if (!list) {
      const group = document.createElement('div');
      group.className = 'predictive-search__result-group';
      group.innerHTML =
        '<div><h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Produkte</h2>' +
        '<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products"></ul></div>';
      wrapper.appendChild(group);
      list = group.querySelector('ul');
    }

    list.innerHTML = matches
      .map(
        (product, position) =>
          `<li id="predictive-search-option-product-${position + 1}" class="predictive-search__list-item" role="option" aria-selected="false">${product.h}</li>`
      )
      .join('');

    const countElement = root.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      const suggestions = wrapper.querySelectorAll(
        '#predictive-search-results-queries-list li, #predictive-search-results-pages-list-desktop li'
      ).length;
      const total = matches.length + suggestions;
      countElement.dataset.totalResults = total;
      countElement.textContent = `${total} rezultate`;
    }
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
